import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import GroupManagement from './GroupManagement';
import NotificationCenter from './notifications/NotificationCenter';
import { ANIMATION_DURATION_MS, IOS_EASING } from '@ext-bookmark/shared/utils';
import { getCurrentVersion, checkForUpdates, UpdateInfo } from '../services/update/updateService';
import { UpdateDialog } from './settings/UpdateDialog';
import { getUnreadNotificationCount } from '../services/notifications/notificationService';
import { useAuth } from '../hooks/useAuth';
import { updateUsername } from '../services/auth/authAdapter';
import DataRecovery from './settings/DataRecovery';
import { usernameSchema, type UsernameFormData } from '../lib/validation-schemas';
import { FormError } from './ui/form-error';

interface SettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onGroupChange?: () => void;
  isMobile?: boolean;
}

export interface SettingsHandle {
  handleBack: () => boolean; // 返回 true 表示已处理，false 表示可以关闭
  canGoBack?: () => boolean; // 可选：检查是否还有子页面
}

type SettingsPage = 'list' | 'groupManagement' | 'notifications' | 'profile' | 'about' | 'dataRecovery';

const Settings = forwardRef<SettingsHandle, SettingsProps>(({ isOpen, onClose, onGroupChange, isMobile = false }, ref) => {
  const [pageStack, setPageStack] = useState<SettingsPage[]>(['list']);
  const [shouldRender, setShouldRender] = useState(isOpen); // 初始值应该等于 isOpen
  const [isAnimating, setIsAnimating] = useState(isOpen); // 初始值应该等于 isOpen
  
  // 版本信息相关状态
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  
  // 通知相关状态
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  
  // 个人资料相关状态
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [isSavingUsername, setIsSavingUsername] = useState(false);
  const usernameInputRef = useRef<HTMLInputElement>(null);

  // 用户名表单
  const {
    register,
    handleSubmit: handleUsernameSubmit,
    formState: { errors: usernameErrors },
    reset: resetUsernameForm,
    setError,
    watch,
  } = useForm<UsernameFormData>({
    resolver: zodResolver(usernameSchema),
    defaultValues: {
      username: profile?.username || '',
    },
  });

  const editingUsername = watch('username');
  
  // 边缘手势相关状态
  const [isEdgeSwipe, setIsEdgeSwipe] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const swipeStartXRef = useRef<number>(0);
  const swipeStartYRef = useRef<number>(0);
  const swipeStartTimeRef = useRef<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // 获取当前页面
  const currentPage = pageStack[pageStack.length - 1];

  // 暴露给父组件的方法
  useImperativeHandle(ref, () => ({
    handleBack: () => {
      if (pageStack.length > 1) {
        // 还有子页面，返回上一页
        setPageStack(pageStack.slice(0, -1));
        setSwipeOffset(0);
        setIsEdgeSwipe(false);
        return true; // 已处理返回
      } else {
        // 已经在列表页，返回 false 表示可以关闭
        return false;
      }
    },
    canGoBack: () => {
      return pageStack.length > 1;
    },
  }));

  // 控制渲染时机，确保动画能正常播放
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // 延迟一帧以确保动画能触发
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, ANIMATION_DURATION_MS); // 与动画时长一致
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // 如果 isOpen 为 true 但 shouldRender 为 false，立即设置（处理初始状态）
  useEffect(() => {
    if (isOpen && !shouldRender) {
      setShouldRender(true);
      setIsAnimating(true);
    }
  }, [isOpen, shouldRender]);

  // 当 Settings 打开时，重置页面栈
  useEffect(() => {
    if (isOpen) {
      setPageStack(['list']);
    }
  }, [isOpen]);

  // 加载当前版本号和未读通知数量
  useEffect(() => {
    if (isOpen) {
      getCurrentVersion().then(version => {
        setCurrentVersion(version);
      });
      loadUnreadNotificationCount();
    }
  }, [isOpen]);

  // 加载未读通知数量
  const loadUnreadNotificationCount = async () => {
    const { count } = await getUnreadNotificationCount();
    setUnreadNotificationCount(count || 0);
  };
  
  // 手动检查更新
  const handleCheckUpdate = async () => {
    setIsCheckingUpdate(true);
    try {
      const info = await checkForUpdates();
      if (info.hasUpdate) {
        setUpdateInfo(info);
      } else {
        // 显示已是最新版本的提示
        alert(`当前版本: ${info.currentVersion}\n已是最新版本！`);
      }
    } catch (error) {
      console.error('[Settings] 检查更新失败:', error);
      alert('检查更新失败，请稍后重试');
    } finally {
      setIsCheckingUpdate(false);
    }
  };

  // 更新表单默认值当 profile 变化时
  useEffect(() => {
    if (profile) {
      resetUsernameForm({ username: profile.username || '' });
    }
  }, [profile, resetUsernameForm]);

  // 开始编辑用户名
  const handleStartEdit = () => {
    if (profile) {
      resetUsernameForm({ username: profile.username || '' });
      setIsEditingUsername(true);
      // 延迟聚焦，确保输入框已渲染
      setTimeout(() => {
        usernameInputRef.current?.focus();
      }, 100);
    }
  };

  // 保存用户名
  const onSubmitUsername = async (data: UsernameFormData) => {
    if (!profile) return;

    // 如果用户名没有变化，直接退出编辑模式
    if (data.username.trim() === profile.username) {
      setIsEditingUsername(false);
      return;
    }

    setIsSavingUsername(true);

    try {
      const result = await updateUsername(data.username.trim(), profile?.id);
      if (result.success) {
        // 使 profile 查询失效，触发重新获取（React Query 会自动处理）
        if (profile?.id) {
          queryClient.invalidateQueries({ queryKey: ['auth', 'profile', profile.id] });
        }
        setIsEditingUsername(false);
      } else {
        // 设置表单错误
        resetUsernameForm({ username: data.username });
        setError('username', { message: result.error || '保存失败，请重试' });
      }
    } catch (error: any) {
      console.error('[Settings] 保存用户名失败:', error);
      resetUsernameForm({ username: data.username });
      setError('username', { message: error.message || '保存失败，请重试' });
    } finally {
      setIsSavingUsername(false);
    }
  };

  // 取消编辑用户名
  const handleCancelEdit = () => {
    setIsEditingUsername(false);
    if (profile) {
      resetUsernameForm({ username: profile.username || '' });
    }
  };

  // 当页面切换或关闭时，重置编辑状态
  useEffect(() => {
    if (currentPage !== 'profile') {
      setIsEditingUsername(false);
      if (profile) {
        resetUsernameForm({ username: profile.username || '' });
      }
    }
  }, [currentPage, profile, resetUsernameForm]);
  
  // 当设置页面关闭时，重置到列表页
  const handleClose = () => {
    setPageStack(['list']);
    setSwipeOffset(0);
    setIsEdgeSwipe(false);
    setUpdateInfo(null);
    onClose();
  };

  // 处理页面切换（进入新页面）
  const handlePageChange = (page: SettingsPage) => {
    setPageStack([...pageStack, page]);
    setSwipeOffset(0);
  };

  // 处理返回上一页（内部使用）
  const handleBack = () => {
    if (pageStack.length > 1) {
      // 还有子页面，返回上一页
      setPageStack(pageStack.slice(0, -1));
      setSwipeOffset(0);
      setIsEdgeSwipe(false);
    } else {
      // 已经在列表页，关闭整个 Settings
      handleClose();
    }
  };

  // 边缘手势处理
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile || currentPage === 'list') return;
    
    const touch = e.touches[0];
    // 检测是否从屏幕左边缘开始（20px内）
    if (touch.clientX < 20) {
      setIsEdgeSwipe(true);
      swipeStartXRef.current = touch.clientX;
      swipeStartYRef.current = touch.clientY;
      swipeStartTimeRef.current = Date.now();
      setSwipeOffset(0);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || !isEdgeSwipe || currentPage === 'list') return;
    
    e.preventDefault();
    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeStartXRef.current;
    const deltaY = Math.abs(touch.clientY - swipeStartYRef.current);
    
    // 如果垂直偏移太大，取消手势
    if (deltaY > 100) {
      setIsEdgeSwipe(false);
      setSwipeOffset(0);
      return;
    }
    
    // 只允许向右滑动（返回）
    if (deltaX > 0) {
      setSwipeOffset(deltaX);
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isEdgeSwipe) return;
    
    const SWIPE_THRESHOLD = 100; // 滑动距离阈值（像素）
    const VELOCITY_THRESHOLD = 0.3; // 速度阈值（像素/毫秒）
    
    const distance = swipeOffset;
    const duration = Date.now() - swipeStartTimeRef.current;
    const velocity = duration > 0 ? distance / duration : 0;
    
    if (distance > SWIPE_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
      // 触发返回
      handleBack();
    } else {
      // 回弹到原位置
      setSwipeOffset(0);
    }
    
    setIsEdgeSwipe(false);
  };

  if (!shouldRender) return null;

  return (
    <>
      {/* 背景遮罩 */}
      <div
        className={`fixed inset-0 z-50 bg-black/50 dark:bg-black/70 transition-opacity ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          transitionDuration: `${ANIMATION_DURATION_MS}ms`,
          transitionTimingFunction: IOS_EASING,
        }}
        onClick={handleClose}
      />
      
      {/* 设置面板 - 从右侧滑入，全屏宽度 */}
      <div
        className="fixed top-0 right-0 bottom-0 z-50 glass w-full flex flex-col overflow-hidden border-l border-border/50"
        style={{
          transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
          transition: `transform ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
        }}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            {currentPage !== 'list' && (
              <button
                onClick={handleBack}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="text-xl font-semibold text-foreground">
              {currentPage === 'list' ? '设置' 
                : currentPage === 'groupManagement' ? '分组管理' 
                : currentPage === 'notifications' ? '通知中心'
                : currentPage === 'profile' ? '个人资料'
                : currentPage === 'dataRecovery' ? '数据恢复'
                : '关于'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-accent/80 transition-colors duration-200 cursor-pointer"
          >
            <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 内容区域 - iOS Push动画效果 */}
        <div 
          ref={contentRef}
          className="flex-1 min-h-0 overflow-hidden relative"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* 列表页面 */}
          <div
            className="absolute inset-0 overflow-y-auto"
            style={{
              transform: currentPage === 'list' 
                ? 'translateX(0)' 
                : (isEdgeSwipe && (currentPage === 'groupManagement' || currentPage === 'notifications' || currentPage === 'profile' || currentPage === 'about' || currentPage === 'dataRecovery'))
                  ? `translateX(${-100 + (swipeOffset / (contentRef.current?.offsetWidth || window.innerWidth)) * 100}%)`
                  : 'translateX(-100%)',
              opacity: currentPage === 'list' 
                ? 1 
                : (isEdgeSwipe && (currentPage === 'groupManagement' || currentPage === 'notifications' || currentPage === 'profile' || currentPage === 'about' || currentPage === 'dataRecovery'))
                  ? Math.min(1, swipeOffset / (contentRef.current?.offsetWidth || window.innerWidth))
                  : 0,
              pointerEvents: currentPage === 'list' ? 'auto' : 'none',
              transition: isEdgeSwipe ? 'none' : `transform ${ANIMATION_DURATION_MS}ms ${IOS_EASING}, opacity ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
            }}
          >
            <div className="p-4">
              {/* 设置项列表 */}
              <div className="space-y-1">
                <button
                  onClick={() => handlePageChange('groupManagement')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">分组管理</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">管理分组和成员</div>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <button
                  onClick={() => handlePageChange('notifications')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center relative">
                      <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      {unreadNotificationCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                          {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                        </span>
                      )}
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">通知中心</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {unreadNotificationCount > 0 ? `${unreadNotificationCount} 条未读通知` : '查看所有通知'}
                      </div>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <button
                  onClick={() => handlePageChange('profile')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">个人资料</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">修改用户名和查看账号信息</div>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                <button
                  onClick={() => handlePageChange('dataRecovery')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">数据恢复</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">恢复备份数据和导入导出</div>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <button
                  onClick={() => handlePageChange('about')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">关于</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">版本信息和更新检查</div>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          {/* 分组管理页面 */}
          <div
            className="absolute inset-0 overflow-y-auto"
            style={{
              transform: currentPage === 'groupManagement'
                ? `translateX(${Math.max(0, swipeOffset)}px)`
                : 'translateX(100%)',
              opacity: currentPage === 'groupManagement' ? 1 : 0,
              pointerEvents: currentPage === 'groupManagement' ? 'auto' : 'none',
              transition: isEdgeSwipe ? 'none' : `transform ${ANIMATION_DURATION_MS}ms ${IOS_EASING}, opacity ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
            }}
          >
            <div className="p-6">
              <GroupManagement
                onClose={handleBack}
                onGroupChange={() => {
                  onGroupChange?.();
                }}
              />
            </div>
          </div>

          {/* 通知中心页面 */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{
              transform: currentPage === 'notifications'
                ? `translateX(${Math.max(0, swipeOffset)}px)`
                : 'translateX(100%)',
              opacity: currentPage === 'notifications' ? 1 : 0,
              pointerEvents: currentPage === 'notifications' ? 'auto' : 'none',
              transition: isEdgeSwipe ? 'none' : `transform ${ANIMATION_DURATION_MS}ms ${IOS_EASING}, opacity ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
            }}
          >
            <NotificationCenter onClose={handleBack} />
          </div>

          {/* 个人资料页面 */}
          <div
            className="absolute inset-0 overflow-y-auto"
            style={{
              transform: currentPage === 'profile'
                ? `translateX(${Math.max(0, swipeOffset)}px)`
                : 'translateX(100%)',
              opacity: currentPage === 'profile' ? 1 : 0,
              pointerEvents: currentPage === 'profile' ? 'auto' : 'none',
              transition: isEdgeSwipe ? 'none' : `transform ${ANIMATION_DURATION_MS}ms ${IOS_EASING}, opacity ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
            }}
          >
            <div className="p-6">
              <div className="space-y-6">
                {/* 账号信息卡片 */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">账号信息</h3>
                  {profile ? (
                    <div className="space-y-4">
                      {/* 用户名编辑区域 */}
                      <div>
                        <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">用户名</label>
                        {!isEditingUsername ? (
                          <div className="flex items-center justify-between">
                            <p className="text-base font-medium text-gray-900 dark:text-gray-100 break-words flex-1">
                              {profile.username || '未设置'}
                            </p>
                            <button
                              onClick={handleStartEdit}
                              className="ml-3 px-3 py-1.5 text-sm text-ios-blue hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                              编辑
                            </button>
                          </div>
                        ) : (
                          <form onSubmit={handleUsernameSubmit(onSubmitUsername)} className="space-y-2">
                            <input
                              {...register('username', { maxLength: 50 })}
                              ref={(e) => {
                                register('username').ref(e);
                                (usernameInputRef as React.MutableRefObject<HTMLInputElement | null>).current = e;
                              }}
                              type="text"
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  handleCancelEdit();
                                }
                              }}
                              placeholder="请输入用户名"
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ios-blue focus:border-transparent"
                              disabled={isSavingUsername}
                              maxLength={50}
                            />
                            <FormError error={usernameErrors.username?.message} />
                            <div className="flex items-center gap-2">
                              <button
                                type="submit"
                                disabled={isSavingUsername}
                                className="px-4 py-2 bg-ios-blue text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                              >
                                {isSavingUsername ? '保存中...' : '保存'}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                disabled={isSavingUsername}
                                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                              >
                                取消
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              支持 emoji 表情，最多 50 个字符（当前：{editingUsername?.length || 0}/50）
                            </p>
                          </form>
                        )}
                      </div>
                      
                      {/* 邮箱（只读） */}
                      {profile.email && (
                        <div>
                          <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">邮箱</label>
                          <p className="text-base text-gray-900 dark:text-gray-100 break-all">{profile.email}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">请先登录以查看个人资料</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* 关于页面 */}
          <div
            className="absolute inset-0 overflow-y-auto"
            style={{
              transform: currentPage === 'about'
                ? `translateX(${Math.max(0, swipeOffset)}px)`
                : 'translateX(100%)',
              opacity: currentPage === 'about' ? 1 : 0,
              pointerEvents: currentPage === 'about' ? 'auto' : 'none',
              transition: isEdgeSwipe ? 'none' : `transform ${ANIMATION_DURATION_MS}ms ${IOS_EASING}, opacity ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
            }}
          >
            <div className="p-6">
              <div className="space-y-6">
                {/* 应用信息 */}
                <div className="text-center py-8">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">项目导航书签</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">版本 {currentVersion || '加载中...'}</p>
                </div>
                
                {/* 版本信息卡片 */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">当前版本</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{currentVersion || '加载中...'}</span>
                  </div>
                </div>
                
                {/* 检查更新按钮 */}
                <button
                  onClick={handleCheckUpdate}
                  disabled={isCheckingUpdate}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
                >
                  {isCheckingUpdate ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>检查中...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>检查更新</span>
                    </>
                  )}
                </button>
                
                {/* 其他信息 */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                    应用会自动检查更新
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 数据恢复页面 */}
          <div
            className="absolute inset-0 overflow-y-auto"
            style={{
              transform: currentPage === 'dataRecovery'
                ? `translateX(${Math.max(0, swipeOffset)}px)`
                : 'translateX(100%)',
              opacity: currentPage === 'dataRecovery' ? 1 : 0,
              pointerEvents: currentPage === 'dataRecovery' ? 'auto' : 'none',
              transition: isEdgeSwipe ? 'none' : `transform ${ANIMATION_DURATION_MS}ms ${IOS_EASING}, opacity ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
            }}
          >
            <div className="p-6">
              <DataRecovery />
            </div>
          </div>
        </div>
      </div>
      
      {/* 更新提示对话框 */}
      {updateInfo && (
        <UpdateDialog
          updateInfo={updateInfo}
          onClose={updateInfo.forceUpdate ? undefined : () => setUpdateInfo(null)}
        />
      )}
    </>
  );
});

Settings.displayName = 'Settings';

export default Settings;
